import { createIcon, Collapse } from 'uikit';
import type { CollapsePropHorizontalSpace } from 'uikit';
import { stopPropagationHelper } from 'utils/stopPropagationHelper';

import cn from 'classnames';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const IconNoneSvg = () => <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" />;

IconNoneSvg.displayName = 'IconNoneSvg';

const IconNone = createIcon({ m: IconNoneSvg, name: 'IconNone', s: IconNoneSvg, xs: IconNoneSvg });

type LeafNavigateType<T> = T extends () => void
    ? {
          to?: never;
          onClick: () => void;
      }
    : {
          to: string;
          onClick?: never;
      };

interface ILeafProps {
    id?: string;
    label: ReactNode;
    rightSide?: ReactNode;
    horizontalSpace?: CollapsePropHorizontalSpace;
    boldStyle?: boolean;
    isActiveLink?: boolean;
    hoverEffect?: boolean;
    isLabelGrey?: boolean;
}

export const ProjectTreeLeaf = <T = any,>({
    id,
    label,
    to,
    rightSide,
    horizontalSpace = '3xl',
    onClick,
    boldStyle,
    isActiveLink,
    hoverEffect,
    isLabelGrey,
}: ILeafProps & LeafNavigateType<T>) => {
    const navigate = useNavigate();

    const handleClick = useCallback(
        (event) => {
            const propagate = id ? stopPropagationHelper({ event }) : true;

            if (propagate) {
                if (onClick) {
                    onClick();
                } else if (to) {
                    navigate(to);
                }
            }
        },
        [id, navigate, onClick, to],
    );

    const title = useMemo(() => {
        if (boldStyle || isLabelGrey) {
            return (
                <div
                    className={`Text Text_lineHeight_m Text_size_s ${
                        isLabelGrey ? 'Text_view_secondary' : 'Text_view_primary'
                    } Collapse-LabelText ${boldStyle ? 'boldStyle' : ''}`}
                >
                    {label}
                </div>
            );
        }

        return label;
    }, [boldStyle, isLabelGrey, label]);

    return (
        <Collapse
            id={id}
            label={title}
            isOpen={false}
            iconPosition="left"
            size="s"
            horizontalSpace={horizontalSpace}
            hoverEffect={hoverEffect}
            icon={IconNone}
            rightSide={rightSide}
            onClick={handleClick}
            className={cn({ isActiveLink })}
        />
    );
};

ProjectTreeLeaf.displayName = 'ProjectTreeLeaf';
